/**
 * Copy and routing shared by the three service pages, per locale.
 * Every visible string is lifted VERBATIM from the pre-redesign pages
 * (TamponBaski / PadPrinting / Tampondruck and their siblings): the hero CTAs
 * and the section headings were identical across the three services of a
 * locale. Breadcrumb labels reuse existing site copy (the old inline nav's
 * home label and the navbar's solutions / service labels).
 * New strings: the breadcrumb landmark label (`crumbLabel`, aria-only) and
 * the hero drawing's pause / play key names (`sceneControls`, aria-only).
 */

/** @typedef {'pad' | 'screen' | 'assembly'} ServiceKey */
/** @typedef {'tr' | 'en' | 'de'} Lang */

/** @type {ReadonlyArray<ServiceKey>} */
export const SERVICE_KEYS = Object.freeze(['pad', 'screen', 'assembly'])

/** Locale routes of each service page (mirrors src/lib/routes.js). */
export const SERVICE_ROUTES = Object.freeze({
  pad: { tr: '/tampon-baski', en: '/en/pad-printing', de: '/de/tampondruck' },
  screen: { tr: '/serigrafi', en: '/en/screen-printing', de: '/de/siebdruck' },
  assembly: { tr: '/montaj-trim', en: '/en/assembly-trim', de: '/de/montage-trim' },
})

/** Analytics `service` dimension sent with the RFQ form. */
export const SERVICE_ANALYTICS_IDS = Object.freeze({
  pad: 'pad_printing',
  screen: 'screen_printing',
  assembly: 'assembly_trim',
})

export const SERVICE_CHROME = Object.freeze({
  tr: {
    crumbLabel: 'Sayfa yolu',
    home: 'Ana Sayfa',
    homeTo: '/',
    solutions: 'Çözümlerimiz',
    solutionsHref: '/#services',
    names: { pad: 'Tampon Baskı', screen: 'Serigrafi Baskı', assembly: 'Montaj & Trim' },
    ctaPrimary: 'Teklif Al',
    ctaSecondary: 'Avantajları İncele',
    sceneControls: { pause: 'Çizimi duraklat', play: 'Çizimi oynat' },
    advantagesTitle: 'Avantajlarımız',
    applicationsTitle: 'Uygulama Alanları',
    portfolioTitle: 'Çalışmalarımızdan Örnekler',
    contactTitle: 'Projenizi Konuşalım',
    relatedTitle: 'İlgili Hizmetler',
  },
  en: {
    crumbLabel: 'Breadcrumb',
    home: 'Home',
    homeTo: '/en',
    solutions: 'Solutions',
    solutionsHref: '/en#services',
    names: { pad: 'Pad Printing', screen: 'Screen Printing', assembly: 'Assembly & Trim' },
    ctaPrimary: 'Request a Quote',
    ctaSecondary: 'View Advantages',
    sceneControls: { pause: 'Pause illustration', play: 'Play illustration' },
    advantagesTitle: 'Our Advantages',
    applicationsTitle: 'Application Areas',
    portfolioTitle: 'Examples of Our Work',
    contactTitle: "Let's Discuss Your Project",
    relatedTitle: 'Related Services',
  },
  de: {
    crumbLabel: 'Brotkrümelnavigation',
    home: 'Startseite',
    homeTo: '/de',
    solutions: 'Lösungen',
    solutionsHref: '/de#services',
    names: { pad: 'Tampondruck', screen: 'Siebdruck', assembly: 'Montage & Trim' },
    ctaPrimary: 'Angebot anfordern',
    ctaSecondary: 'Vorteile ansehen',
    sceneControls: { pause: 'Illustration anhalten', play: 'Illustration abspielen' },
    advantagesTitle: 'Unsere Vorteile',
    applicationsTitle: 'Einsatzgebiete',
    portfolioTitle: 'Beispiele unserer Arbeit',
    contactTitle: 'Lassen Sie uns über Ihr Projekt sprechen',
    relatedTitle: 'Verwandte Dienstleistungen',
  },
})

/** The gallery every service page showed (same five plates on all nine pages). */
export const SERVICE_PORTFOLIO_IMAGES = Object.freeze([
  'portfolio-new-1.webp',
  'portfolio-7.webp',
  'portfolio-11.webp',
  'portfolio-new-4.webp',
  'portfolio-10.webp',
])

/** document.title restored when a service page unmounts (per locale, verbatim). */
export const RESET_TITLES = Object.freeze({
  tr: 'Ankaplastik | Endüstriyel Tampon & Serigrafi Baskı Çözümleri - Bursa',
  en: 'Ankaplastik | Industrial Pad & Screen Printing Solutions - Turkey',
  de: 'Ankaplastik | Industrielle Tampon- & Siebdrucklösungen - Türkei',
})
